import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  api,
  formatApiError,
  getActiveLedgerId,
  setActiveLedgerId as persistLedgerId,
} from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [households, setHouseholds] = useState([]);
  const [activeLedgerId, setLedgerIdState] = useState(getActiveLedgerId());

  const setActiveLedger = useCallback((ledgerId) => {
    persistLedgerId(ledgerId || null);
    setLedgerIdState(ledgerId || null);
  }, []);

  const refreshHouseholds = useCallback(async () => {
    try {
      const r = await api.get("/households");
      setHouseholds(r.data || []);
      return r.data || [];
    } catch {
      setHouseholds([]);
      return [];
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      // Load households & verify active ledger is still valid
      const list = await refreshHouseholds();
      const lid = getActiveLedgerId();
      if (lid && !list.find((h) => h.id === lid)) {
        // Stale household -> revert to personal
        persistLedgerId(null);
        setLedgerIdState(null);
      }
      // v3.15 — if the user has ≥1 household but their active ledger is
      // "Personal" (null), auto-switch to their first (most-recently-created)
      // household. Personal ledger is now hidden from the switcher, so
      // leaving anyone stuck on it would strand them.
      if (!getActiveLedgerId() && list.length > 0) {
        persistLedgerId(list[0].id);
        setLedgerIdState(list[0].id);
      }
    } catch {
      setUser(null);
      setHouseholds([]);
      persistLedgerId(null);
      setLedgerIdState(null);
    } finally {
      setLoading(false);
    }
  }, [refreshHouseholds]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const r = await api.post("/auth/login", { email, password });
      setUser(r.data);
      await refreshHouseholds();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const r = await api.post("/auth/register", { email, password, name });
      setUser(r.data);
      await refreshHouseholds();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout", {});
    } catch {
      // ignore
    }
    setUser(null);
    setHouseholds([]);
    persistLedgerId(null);
    setLedgerIdState(null);
  };

  const beginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const completeGoogle = async (sessionId) => {
    try {
      const r = await api.post("/auth/google-callback", { session_id: sessionId });
      setUser(r.data);
      await refreshHouseholds();
      return { ok: true, user: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const updateProfile = async (patch) => {
    try {
      const r = await api.patch("/auth/profile", patch);
      setUser(r.data);
      return { ok: true, user: r.data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const activeHousehold = households.find((h) => h.id === activeLedgerId) || null;
  const activeLedgerLabel = activeHousehold ? activeHousehold.name : "Personal";
  const activeRole = activeHousehold ? activeHousehold.my_role : "owner";
  const canWrite = activeRole !== "viewer" && activeRole !== "executor";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        households,
        activeLedgerId,
        activeHousehold,
        activeLedgerLabel,
        activeRole,
        canWrite,
        setActiveLedger,
        refreshHouseholds,
        login,
        register,
        logout,
        beginGoogle,
        completeGoogle,
        updateProfile,
        refresh: checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
